import { calculatorItemLabel } from "./calculator-layer-inspector-contract.js";
import { calculatorDataText as t } from "./calculator-data-i18n.js";
import {
    provenanceFactCardMarkup,
    provenanceFactCardPresentationStatus,
    provenanceFactValueMarkup,
} from "./provenance-fact-card.js";
import {
    calculatorLootFactChannel,
    parseCalculatorLootSubject,
} from "./calculator-loot-subject.js";
import {
    calculatorItemQuantityHistoryIsPresentationRelevant,
} from "./calculator-item-quantity-presentation.js";
import {
    calculatorLootProvenanceFactLabel,
} from "./calculator-loot-provenance-presentation.js";
import {
    calculatorRateAlternativeLabel,
} from "./calculator-rate-alternative-presentation.js";

function escapeText(value) {
    return String(value ?? "")
        .replaceAll("&", "&amp;")
        .replaceAll("<", "&lt;")
        .replaceAll(">", "&gt;")
        .replaceAll('"', "&quot;")
        .replaceAll("'", "&#39;");
}

function groupLabel(groupId, dataPresentation) {
    const label = dataPresentation?.groupsByGroupId?.[groupId]?.label;
    return typeof label === "string" && label.trim() ? label.trim() : groupId;
}

function itemLabel(itemId, customCatalogItems, dataPresentation) {
    const label = dataPresentation?.itemsByItemId?.[String(itemId)]?.label;
    return typeof label === "string" && label.trim()
        ? label.trim()
        : calculatorItemLabel(itemId, customCatalogItems);
}

function groupPresentationRanks(projection) {
    return new Map((projection.presentationOrder?.groups ?? []).map((entry) => [
        entry.groupId,
        entry.rank,
    ]));
}

function focusedTraces(projection, subject, fishingSource) {
    const traces = projection.provenanceTraces ?? [];
    if (!Array.isArray(traces)) {
        throw new TypeError("projection.provenanceTraces must be an array");
    }
    const scoped = (channel) => traces.filter((trace) => (
        calculatorLootFactChannel(trace.factKey, subject, fishingSource) === channel
    ));
    return Object.freeze({
        presence: Object.freeze(scoped("presence")),
        rate: Object.freeze(scoped("rate")),
    });
}

function traceSortValue(trace, channel, ranks) {
    const factKey = trace.factKey;
    const kindRanks = {
        presence: {
            groupPresence: 0,
            itemPresence: 2,
            groupMembership: 3,
        },
        rate: {
            groupRate: 0,
            itemRate: 1,
            itemQuantity: 2,
            itemQuantityDefault: 2,
        },
    };
    const kindRank = kindRanks[channel]?.[factKey.kind];
    return Object.freeze({
        kindRank: kindRank ?? Number.MAX_SAFE_INTEGER,
        groupRank: ranks.get(factKey.groupId) ?? Number.MAX_SAFE_INTEGER,
        groupId: factKey.groupId ?? "",
        itemId: factKey.itemId ?? Number.MAX_SAFE_INTEGER,
        branchId: factKey.branchId ?? "",
        traceId: trace.traceId,
    });
}

function compareTrace(left, right, channel, ranks) {
    const leftValue = traceSortValue(left, channel, ranks);
    const rightValue = traceSortValue(right, channel, ranks);
    return leftValue.kindRank - rightValue.kindRank
        || leftValue.groupRank - rightValue.groupRank
        || leftValue.groupId.localeCompare(rightValue.groupId)
        || leftValue.itemId - rightValue.itemId
        || leftValue.branchId.localeCompare(rightValue.branchId)
        || leftValue.traceId.localeCompare(rightValue.traceId);
}

function laneLabel(factKind, factKey, dataPresentation, path) {
    const group = typeof factKey.groupId === "string"
        ? groupLabel(factKey.groupId, dataPresentation)
        : null;
    return calculatorLootProvenanceFactLabel(factKind, {
        groupLabel: group,
        path,
    });
}

function selectedRateAlternativeKeys(projection) {
    return new Set((projection.rateAlternativeResolutions ?? []).flatMap((resolution) => (
        resolution.kind === "automatic" || resolution.kind === "override"
            ? [`${resolution.familyId}\u0000${resolution.alternativeId}`]
            : []
    )));
}

function traceRateAlternative(trace, path) {
    const alternatives = new Map();
    for (const [candidateIndex, candidate] of trace.candidates.entries()) {
        const alternative = candidate.operation?.alternative;
        if (!alternative) {
            continue;
        }
        const key = `${alternative.familyId}\u0000${alternative.alternativeId}`;
        alternatives.set(key, {
            alternative,
            path: `${path}.candidates[${candidateIndex}].operation.alternative`,
        });
    }
    if (alternatives.size > 1) {
        throw new TypeError(`${path} contains conflicting rate alternatives`);
    }
    return alternatives.values().next().value ?? null;
}

function createLanes(traces, channel, resolver, projection, dataPresentation) {
    if (!resolver) {
        return Object.freeze([]);
    }
    const ranks = groupPresentationRanks(projection);
    const selectedAlternatives = selectedRateAlternativeKeys(projection);
    const lanes = traces
        .slice()
        .sort((left, right) => compareTrace(left, right, channel, ranks))
        .map((trace, laneIndex) => {
            const lanePath = `lootEditor.provenanceGraph.${channel}[${laneIndex}]`;
            const orderedTrace = resolver.resolveOrderedTrace(
                trace.traceId,
                lanePath,
            );
            const alternative = traceRateAlternative(orderedTrace, lanePath);
            const alternativeKey = alternative
                ? `${alternative.alternative.familyId}\u0000${alternative.alternative.alternativeId}`
                : null;
            return Object.freeze({
                laneId: trace.traceId,
                label: laneLabel(
                    orderedTrace.factKind,
                    orderedTrace.factKey,
                    dataPresentation,
                    lanePath,
                ),
                factKind: orderedTrace.factKind,
                path: lanePath,
                trace: orderedTrace,
                conditionLabel: alternative
                    ? calculatorRateAlternativeLabel(
                        alternative.alternative,
                        alternative.path,
                    )
                    : null,
                activeCondition: alternativeKey !== null
                    && selectedAlternatives.has(alternativeKey),
            });
        });
    const quantityProvenanceRelevant = lanes
        .filter(({ factKind }) => (
            factKind === "itemQuantity" || factKind === "itemQuantityDefault"
        ))
        .map(({ path, trace }) => calculatorItemQuantityHistoryIsPresentationRelevant(
            trace.candidates,
            `${path}.candidates`,
        ))
        .some(Boolean);
    const visibleLanes = lanes.filter(({ factKind }) => (
        (factKind !== "itemQuantity" && factKind !== "itemQuantityDefault")
            || quantityProvenanceRelevant
    ));
    const labelCounts = new Map();
    for (const { label } of visibleLanes) {
        labelCounts.set(label, (labelCounts.get(label) ?? 0) + 1);
    }
    const labelPositions = new Map();
    return Object.freeze(visibleLanes.map(({ path: _path, ...lane }) => {
        const count = labelCounts.get(lane.label);
        const position = (labelPositions.get(lane.label) ?? 0) + 1;
        labelPositions.set(lane.label, position);
        return Object.freeze({
            ...lane,
            timelineLabel: (count > 1
                ? `${lane.label} (${position}/${count})`
                : lane.label) + (lane.conditionLabel ? ` · ${lane.conditionLabel}` : ""),
        });
    }));
}

function graphSubjectLabel(context, customCatalogItems, dataPresentation) {
    if (!context) {
        return null;
    }
    if (context.kind === "group") {
        return groupLabel(context.groupId, dataPresentation);
    }
    const item = itemLabel(context.itemId, customCatalogItems, dataPresentation);
    return context.groupId
        ? `${item} · ${groupLabel(context.groupId, dataPresentation)}`
        : item;
}

const provenanceGraphStates = new WeakSet();

export function createCalculatorLootProvenanceGraphState({
    projection,
    provenanceResolver = null,
    fishingSource,
    selectedLootSubject = null,
    customCatalogItems = [],
    dataPresentation = null,
}) {
    if (!projection || typeof projection !== "object") {
        throw new TypeError("loot provenance graph requires a projection");
    }
    const context = parseCalculatorLootSubject(
        selectedLootSubject,
        "selectedLootSubject",
    );
    const traces = focusedTraces(projection, context, fishingSource);
    const channels = Object.freeze([
        Object.freeze({
            kind: "presence",
            label: t("calculator.data.provenance_graph.presence", "Presence"),
            description: t(
                "calculator.data.provenance_graph.presence_description",
                "Direct presence facts for only the selected fishing group or loot row.",
            ),
            lanes: createLanes(
                traces.presence,
                "presence",
                provenanceResolver,
                projection,
                dataPresentation,
            ),
        }),
        Object.freeze({
            kind: "rate",
            label: t("calculator.data.provenance_graph.rate", "Rate"),
            description: t(
                "calculator.data.provenance_graph.rate_description",
                "Direct rate facts for the selected fishing group or loot row, plus quantity facts for a selected loot row.",
            ),
            lanes: createLanes(
                traces.rate,
                "rate",
                provenanceResolver,
                projection,
                dataPresentation,
            ),
        }),
    ]);
    const state = Object.freeze({
        context,
        subjectLabel: graphSubjectLabel(context, customCatalogItems, dataPresentation),
        hasResolver: Boolean(provenanceResolver),
        channels,
        renderKey: JSON.stringify({ context, channels }),
    });
    provenanceGraphStates.add(state);
    return state;
}

function candidateControls(candidate) {
    const operation = candidate.operation;
    if (candidate.layer.kind === "user" && operation.operationId) {
        return `<button class="btn btn-outline btn-xs" type="button" data-action="revert-user-operation" data-operation-id="${escapeText(operation.operationId)}">${escapeText(t("calculator.data.action.revert_user_operation", "Revert user operation"))}</button>`;
    }
    if (
        candidate.layer.kind === "user"
        || candidate.status === "refuted"
        || !operation.factId
    ) {
        return "";
    }
    const factId = escapeText(operation.factId);
    return '<div class="grid min-w-0 gap-2">'
        + `<label class="form-control min-w-0"><span class="label-text text-xs">${escapeText(t("calculator.data.field.refutation_reason", "Refutation reason code"))}</span>`
        + `<input class="input input-bordered input-xs min-w-0 w-full" name="refute-reason-${factId}" placeholder="${escapeText(t("calculator.data.placeholder.reason_code", "Enter a reason code"))}" data-refute-reason-for="${factId}"></label>`
        + `<button class="btn btn-outline btn-xs" type="button" data-action="refute-evidence" data-fact-id="${factId}">${escapeText(t("calculator.data.action.refute", "Refute with user layer"))}</button>`
        + '</div>';
}

function candidateNode(candidate, effectiveCard) {
    const actions = candidateControls(candidate);
    const presentedStatus = provenanceFactCardPresentationStatus({
        card: candidate.card,
        status: candidate.status,
        effectiveCard,
    });
    return `<li class="fishy-provenance-flow__node" data-loot-provenance-graph-node data-candidate-status="${escapeText(presentedStatus)}">`
        + provenanceFactCardMarkup({
            card: candidate.card,
            effectiveCard,
            status: candidate.status,
            actionsMarkup: actions || null,
        })
        + '</li>';
}

function finalNode(lane) {
    const effective = lane.trace.candidates.find(({ status }) => status === "effective") ?? null;
    const resultAttributes = effective
        ? ` data-effective-fact-id="${escapeText(lane.trace.effectiveFactId ?? "")}"`
        : " data-provenance-result-empty";
    const valueRow = effective
        ? effective.card.rows[0]
        : Object.freeze({
            label: t("calculator.data.metadata.value", "Value"),
            value: t("calculator.data.provenance_graph.no_effective", "No effective fact"),
        });
    const resultLabel = effective
        ? t("calculator.data.provenance.final", "Final")
        : t("calculator.data.provenance_graph.result", "Result");
    const activeCondition = lane.activeCondition
        ? `<span class="inline-flex items-center gap-1 text-xs font-semibold text-success" data-loot-provenance-active-condition><span class="status status-success status-xs" aria-hidden="true"></span>${escapeText(t(
            "calculator.data.provenance_graph.active_condition",
            "Active condition",
        ))}</span>`
        : "";
    const condition = lane.conditionLabel
        ? `<div class="rounded-box border border-base-300 bg-base-200/70 px-3 py-2" data-loot-provenance-final-condition tabindex="0" aria-label="${escapeText(lane.conditionLabel)}" data-fishy-tooltip="${escapeText(lane.conditionLabel)}"><span class="block text-[0.65rem] font-semibold uppercase tracking-wide text-base-content/75">${escapeText(t(
            "calculator.data.metadata.conditions",
            "Conditions",
        ))}</span><strong class="block truncate text-sm">${escapeText(lane.conditionLabel)}</strong></div>`
        : "";
    return `<li class="fishy-provenance-flow__node" data-loot-provenance-graph-node data-loot-provenance-graph-result${resultAttributes}${lane.activeCondition ? " data-active-condition" : ""}>`
        + '<article class="card card-xs card-border bg-base-100" data-loot-provenance-graph-final-card>'
        + '<div class="card-body">'
        + `<div class="flex min-w-0 items-center justify-between gap-2"><h4 class="min-w-0 text-sm font-semibold"><span class="sr-only">${escapeText(resultLabel)}: </span>${escapeText(lane.label)}</h4>${activeCondition}</div>`
        + condition
        + provenanceFactValueMarkup(valueRow)
        + '</div></article></li>';
}

function renderLane(lane) {
    const descendingCandidates = lane.trace.candidates.slice().reverse();
    const effectiveCard = lane.trace.candidates
        .find(({ status }) => status === "effective")
        ?.card ?? null;
    return `<section class="flex min-w-max items-stretch" data-loot-provenance-graph-lane data-trace-id="${escapeText(lane.laneId)}">`
        + `<ol class="fishy-provenance-flow" data-loot-provenance-graph-timeline data-flow-direction="toward-start" aria-label="${escapeText(lane.timelineLabel)}">`
        + finalNode(lane)
        + descendingCandidates.map((candidate) => candidateNode(candidate, effectiveCard)).join("")
        + '</ol></section>';
}

function renderChannel(channel, graph) {
    const empty = !graph.context
        ? t(
            "calculator.data.provenance_graph.select_subject",
            "Select a fishing group or loot row to inspect its provenance.",
        )
        : !graph.hasResolver
            ? t(
                "calculator.data.provenance_graph.details_unavailable",
                "Provenance details are unavailable for this fact.",
            )
            : t(
                "calculator.data.provenance_graph.no_facts",
                "No provenance facts are loaded for this channel.",
            );
    const lanes = channel.lanes.length
        ? channel.lanes.map(renderLane).join("")
        : `<p class="py-4 text-sm opacity-65">${escapeText(empty)}</p>`;
    return `<div data-loot-provenance-graph-channel data-provenance-channel="${channel.kind}">`
        + '<article class="card card-border bg-base-100" data-loot-provenance-graph-root>'
        + '<div class="card-body">'
        + `<h3 class="card-title text-base">${escapeText(channel.label)}</h3>`
        + `<p class="text-xs opacity-65">${escapeText(channel.description)}</p>`
        + '</div></article>'
        + `<div data-loot-provenance-graph-scroller tabindex="0" role="region" aria-label="${escapeText(t(
            "calculator.data.provenance_graph.scroll_label",
            "{ $channel } provenance graph",
            { channel: channel.label },
        ))}">`
        + `<div data-loot-provenance-graph-lanes>${lanes}</div>`
        + '</div></div>';
}

export function renderCalculatorLootProvenanceGraph(value) {
    if (!provenanceGraphStates.has(value)) {
        throw new TypeError(
            "loot provenance graph state must come from createCalculatorLootProvenanceGraphState()",
        );
    }
    const subject = value.subjectLabel
        ? t(
            "calculator.data.provenance_graph.subject",
            "Provenance for { $subject }",
            { subject: value.subjectLabel },
        )
        : t("calculator.data.provenance_graph.heading", "Loot provenance");
    return '<section class="grid min-w-0 gap-3" aria-labelledby="calculator-loot-provenance-heading" data-loot-provenance-graph>'
        + '<header>'
        + `<h3 class="text-base font-semibold" id="calculator-loot-provenance-heading">${escapeText(subject)}</h3>`
        + `<p class="text-sm opacity-70">${escapeText(t(
            "calculator.data.provenance_graph.description",
            "Each lane starts with its final value. Moving right shows candidates from highest to lowest precedence.",
        ))}</p>`
        + '</header>'
        + value.channels.map((channel) => renderChannel(channel, value)).join("")
        + '</section>';
}
